import Axios, { AxiosResponse, AxiosRequestConfig, AxiosInstance } from 'axios';
import eventBus from '@/common/bus';
import Shared from '@/common/shared';
import { getModule } from 'vuex-module-decorators';
import StoreVuexModule, { UserModel } from '@/stores/StoreVuexModule';
import store from '@/store';
import { getInstance } from 'recaptcha-v3';
import LoginService from './login.service';
import { identifyUser } from '@/services/segment.service';

const storeModule = getModule(StoreVuexModule, store);

export function getBaseUrl(): string {
    return storeModule.baseApiUrl;
}

function parseJwt(token: string): UserModel | null {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
}

async function checkRecaptcha(user: UserModel, action: string): Promise<UserModel> {
    if (!user.roleCode || user.roleCode === 'Unauthorised' || user.exp <= new Date()) {
        const loginService = new LoginService();
        const recaptcha = getInstance();
        const token = await recaptcha.execute(action);
        const jwt = await loginService.recaptchaLogin(token);

        if (jwt && jwt !== '') {
            user = storeModule.updateUserModel(jwt);

            const userObject = parseJwt(user.token);
            if (userObject) {
                identifyUser(userObject.credentialIdentifier, userObject.retailerId.toString());
            }
        } else {
            eventBus.$emit(
                'errorHandler',
                Shared.messages.UNABLE_TO_REACH_SERVER,
                true,
            );
        }
    }

    return user;
}

export async function getBearerToken(action: string, isAnonymous: boolean = false): Promise<string> {
    if (isAnonymous) {
        return storeModule.user.token;
    }

    const user = await checkRecaptcha(storeModule.user, action);
    if (user.roleCode !== 'Unauthorised') {
        return user.token;
    } else {
        return '';
    }
}

/**
 * Create custom instance of Axios with custom changes
 *
 * @returns {AxiosInstance}
 */
export function createAxiosInstance(): AxiosInstance {
    function fnServiceUnavailable(): void {
        eventBus.$emit(
            'errorHandler',
            Shared.messages.SERVICE_UNAVAILABLE,
            true,
        );
    }

    const axios = Axios.create({
        transformResponse: (data: any): any => {
            let res: any = data;
            try {
                if (typeof data !== 'string') {
                    // if string pass as it is.
                    return data;
                }
                // else json parse the response
                res = JSON.parse(data);

            } catch (ex) {
                res = data;
            }
            return res;
        },
        validateStatus: (status: number) => {
            return (status >= 200 && status < 300) || status === 404 || status === 401 || status === 403;
        },
    });

    axios.interceptors.request.use(async (config: AxiosRequestConfig) => {
        // set base URL
        config.baseURL = getBaseUrl();
        let token = '';
        if ('x-IsAnonymous' in config.headers) {
            token = await getBearerToken('check_recaptcha', true);
        } else {
            token = await getBearerToken('check_recaptcha');
        }

        if (token && token !== '') {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    axios.interceptors.response.use(
        (res: AxiosResponse<any>) => {

            // auth check
            if (res.status === 401 || res.status === 403) {
                const s = getModule(StoreVuexModule, store);
                s.updateUserModel(null);
            }

            // server error codes
            if (res.status >= 500 && res.status <= 509) {
                fnServiceUnavailable();
            }
            if (res.status === 404) {
                res.data = null;
            }
            return res;
        },
        (error: any) => {
            if (error.message && error.message === 'Network Error') {
                fnServiceUnavailable();
            }

            return Promise.reject(error);
        },
    );

    // return instance
    return axios;
}
